// BeFit v2 scoring. Answers are { [scenarioId]: { most: "LTO"|"COM"|"STP"|"ATD", least: <another style> } } — the
// style codes, not the on-screen position, so a stored answer set can always be re-scored whatever order the
// options were shown in.
//
// Each scenario gives the style marked MOST +1 and the style marked LEAST −1. Over the 24 scenarios a style's raw
// score therefore runs −24…+24, and the four raw scores always sum to zero (the choice is forced — "ipsative").
// Emphasis maps raw onto 0–100 with 50 meaning "no more and no less than the other styles". It is a RELATIVE
// profile: it says where a person leans, not how much of any quality they have, and it cannot be "high" on all four.
import { BF2_SCENARIOS, BF2_DIM_IDS, BF2_STYLES } from "./scenarios.js";

export const BF2_VERSION = 2;
export const BF2_ITEM_COUNT = BF2_SCENARIOS.length;

// Target profiles per role: the emphasis a role is *hypothesised* to reward. Each sums to 200 (mean 50) so they sit
// on the same scale as a candidate's profile. These are author judgements, NOT derived from performance data —
// they exist so an employer can see how closely a profile resembles a role's working style. They are not pass marks.
export const BF2_ROLE_TARGETS = {
  balanced:   { label:"Balanced / General",    LTO:50, COM:50, STP:50, ATD:50 },
  sales:      { label:"Sales / Client-facing", LTO:45, COM:68, STP:55, ATD:32 },
  support:    { label:"Customer Support",      LTO:35, COM:65, STP:62, ATD:38 },
  operations: { label:"Operations / Admin",    LTO:40, COM:38, STP:52, ATD:70 },
  supervisor: { label:"Team Lead / Supervisor",LTO:68, COM:56, STP:45, ATD:31 },
};

// A starting suggestion for which role profile to compare against, from the job title the candidate was invited for.
// Returns a key of BF2_ROLE_TARGETS (never "balanced") or null. It is only a default: the reviewer can change it.
export function suggestRole(title) {
  const t = String(title || "").toLowerCase();
  if (!t.trim()) return null;
  if (/\b(team lead|supervisor|manager|head of|lead)\b/.test(t)) return "supervisor";
  if (/\b(customer|support|service|helpdesk|help desk|care|call cent)/.test(t)) return "support";
  if (/\b(sales|business development|account (exec|manager)|relationship)/.test(t)) return "sales";
  if (/\b(operations?|admin|back.?office|logistics|coordinator|processing)/.test(t)) return "operations";
  return null;
}

// How closely a profile resembles a role's target. Descriptive wording only — never "recommended / not recommended".
export const BF2_MATCH_BANDS = [
  { min:0,  max:50,  label:"Different working style", color:"#6b7280", bg:"#f9fafb", border:"#e5e7eb",
    desc:"This profile leans quite differently from the style this role is described as rewarding. That is not a verdict on the person — use the interview to explore how they would approach the role's day-to-day demands." },
  { min:50, max:65,  label:"Partial match",           color:"#d97706", bg:"#fffbeb", border:"#fde68a",
    desc:"Some of the profile lines up with the role's working style and some does not. The areas of difference are good topics for interview questions." },
  { min:65, max:80,  label:"Good match",              color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe",
    desc:"The profile broadly resembles the working style this role is described as rewarding, with a few areas that lean differently." },
  { min:80, max:101, label:"Close match",             color:"#059669", bg:"#f0fdf4", border:"#bbf7d0",
    desc:"The profile closely resembles the working style this role is described as rewarding." },
];

const toEmphasis = (raw, answered) => {
  // Scale by the scenarios actually answered so a partial set is not dragged towards 50.
  const span = Math.max(answered, 1);
  return Math.round(((raw + span) / (2 * span)) * 100);
};

// Pull out the valid answers only: a known scenario, two different known styles.
function clean(answers) {
  const out = {};
  for (const sc of BF2_SCENARIOS) {
    const a = answers?.[sc.id];
    if (a && BF2_STYLES[a.most] && BF2_STYLES[a.least] && a.most !== a.least) out[sc.id] = { most: a.most, least: a.least };
  }
  return out;
}

// Points lost per point of average gap between the profile and the role's target. Calibrated against a simulated
// random responder (tests/befit-v2.test.mjs): a person answering with no preference at all sits around 50 — "Partial
// match" at best — and only a tight fit reaches "Good" or "Close". At the earlier slope of 2, ~90% of random answer
// sets scored "Good" or "Close", because an ipsative profile that leans little is never far from any target.
// The ranking of roles is unaffected by the slope; only what the bands mean.
export const MATCH_SLOPE = 4;

export function matchToRole(emphasis, roleKey = "balanced") {
  const t = BF2_ROLE_TARGETS[roleKey] || BF2_ROLE_TARGETS.balanced;
  const meanAbsDiff = BF2_DIM_IDS.reduce((s, d) => s + Math.abs((emphasis[d] ?? 50) - t[d]), 0) / BF2_DIM_IDS.length;
  const score = Math.max(0, Math.round(100 - meanAbsDiff * MATCH_SLOPE));
  const band = BF2_MATCH_BANDS.find(b => score >= b.min && score < b.max) || BF2_MATCH_BANDS[0];
  return { score, band, roleKey: BF2_ROLE_TARGETS[roleKey] ? roleKey : "balanced", roleLabel: t.label, target: Object.fromEntries(BF2_DIM_IDS.map(d => [d, t[d]])) };
}

export function scoreBehavioralFitV2(answers, roleKey = "balanced") {
  const valid = clean(answers);
  const answered = Object.keys(valid).length;
  const most = { LTO:0, COM:0, STP:0, ATD:0 }, least = { LTO:0, COM:0, STP:0, ATD:0 };
  for (const a of Object.values(valid)) { most[a.most]++; least[a.least]++; }

  const dimResults = {};
  for (const d of BF2_DIM_IDS) {
    const raw = most[d] - least[d];
    dimResults[d] = { most: most[d], least: least[d], raw, emphasis: toEmphasis(raw, answered) };
  }
  const emphasis = Object.fromEntries(BF2_DIM_IDS.map(d => [d, dimResults[d].emphasis]));

  // Rank strongest → weakest. Ties: more MOST picks first, then fewer LEAST picks, then fixed order, so it is deterministic.
  const ranked = [...BF2_DIM_IDS].sort((a, b) =>
    dimResults[b].raw - dimResults[a].raw || dimResults[b].most - dimResults[a].most || dimResults[a].least - dimResults[b].least || BF2_DIM_IDS.indexOf(a) - BF2_DIM_IDS.indexOf(b));
  const spread = dimResults[ranked[0]].emphasis - dimResults[ranked[3]].emphasis;
  const flat = answered > 0 && spread < 12;      // no meaningful lean: say so rather than invent a "primary style"

  const fit = matchToRole(emphasis, roleKey);
  return {
    version: BF2_VERSION,
    answered, total: BF2_ITEM_COUNT, complete: answered === BF2_ITEM_COUNT,
    dimResults,
    scales: emphasis,                                   // same four keys v1 used, so dashboards/radars keep working
    primary: flat ? null : ranked[0],
    secondary: flat ? null : ranked[1],
    least: flat ? null : ranked[3],
    profile: flat ? "Balanced" : `${ranked[0]}-${ranked[1]}`,
    ranked, flat,
    roleKey: fit.roleKey, roleLabel: fit.roleLabel,
    overallFit: fit.score, band: fit.band, target: fit.target,   // `overallFit` kept as the name v1 consumers (grids, ATS lists) already read
  };
}

// A stored assessment is v2 if its answers are keyed by v2 scenario ids. Detecting it from the answers themselves
// (rather than a flag) means assessments saved before this existed, and the v1 32-item results, are never misread.
export function isBeFitV2(a) {
  const ans = a?.answers;
  return !!ans && typeof ans === "object" && !Array.isArray(ans) && Object.keys(ans).some(k => k.startsWith("bf2_"));
}
